import type { AdminUserDetail, SaveAdminUserPayload } from '@/api/adminUsers';

/** Поля карточки администратора — общее состояние для правки и мастера создания. */
export interface AdminFormState {
  name: string;
  lastName: string;
  firstName: string;
  surname: string;
  email: string;
  status: string;
  password: string;
  mt1Server: string;
  roleIds: number[];
  permissions: string[];
}

export const REQUIRED_ADMIN_FIELD_LABELS: Record<string, string> = {
  name: 'Название',
  email: 'Электронная почта',
  roleIds: 'Роли',
};

export function createEmptyAdminState(): AdminFormState {
  return {
    name: '',
    lastName: '',
    firstName: '',
    surname: '',
    email: '',
    status: 'active',
    password: '',
    mt1Server: '',
    roleIds: [],
    permissions: [],
  };
}

export function adminStateFromDetail(detail: AdminUserDetail): AdminFormState {
  return {
    name: detail.name ?? '',
    lastName: detail.lastName ?? '',
    firstName: detail.firstName ?? '',
    surname: detail.surname ?? '',
    email: detail.email ?? '',
    status: detail.status ?? 'active',
    password: '',
    mt1Server: detail.mt1Server ?? '',
    roleIds: detail.roleIds,
    permissions: detail.permissions,
  };
}

/**
 * login не редактируется: он заводится на сервере авторизации, здесь только
 * пробрасывается обратно без изменений (у нового администратора — null,
 * сервер подставит электронную почту).
 */
export function adminStateToPayload(state: AdminFormState, login: string | null): SaveAdminUserPayload {
  return {
    name: state.name.trim(),
    lastName: state.lastName.trim() || null,
    firstName: state.firstName.trim() || null,
    surname: state.surname.trim() || null,
    email: state.email.trim(),
    status: state.status,
    login: login || null,
    password: state.password || null,
    mt1Server: state.mt1Server || null,
    roleIds: state.roleIds,
    permissions: state.permissions,
  };
}

export function adminStateEquals(a: AdminFormState, b: AdminFormState): boolean {
  const sameNumbers = (x: number[], y: number[]) => x.length === y.length && x.every((v) => y.includes(v));
  const sameStrings = (x: string[], y: string[]) => x.length === y.length && x.every((v) => y.includes(v));
  return (
    a.name === b.name &&
    a.lastName === b.lastName &&
    a.firstName === b.firstName &&
    a.surname === b.surname &&
    a.email === b.email &&
    a.status === b.status &&
    a.password === b.password &&
    a.mt1Server === b.mt1Server &&
    sameNumbers(a.roleIds, b.roleIds) &&
    sameStrings(a.permissions, b.permissions)
  );
}

/** Обязательные поля шага мастера: 1 — профиль, 2 — единый вход, 3 — роли, 4 — проверка. */
export function validateAdminStep(step: number, state: AdminFormState): string[] {
  const missing: string[] = [];
  if (step === 1) {
    if (!state.name.trim()) missing.push('name');
    if (!state.email.trim()) missing.push('email');
  }
  if (step === 3 && state.roleIds.length === 0) missing.push('roleIds');
  return missing;
}

/** Минимальная длина пароля — 1:1 правило `password: nullable|string|min:8` на бэке. */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Пустой пароль — валиден: у существующего это «не менять», у нового —
 * «сгенерировать». Ошибка только у слишком короткого, и показывается сразу,
 * не дожидаясь ответа сервера.
 */
export function validateAdminPassword(state: AdminFormState): string | null {
  if (!state.password) return null;
  return state.password.length < PASSWORD_MIN_LENGTH
    ? `Пароль не короче ${PASSWORD_MIN_LENGTH} символов.`
    : null;
}
